import {type ReactNode} from 'react';

import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CampaignIcon from '@mui/icons-material/Campaign';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import GridViewIcon from '@mui/icons-material/GridView';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import Home from '@mui/icons-material/Home';
import LightbulbOutlineIcon from '@mui/icons-material/LightbulbOutlined';
import LinkIcon from '@mui/icons-material/Link';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import QuizIcon from '@mui/icons-material/Quiz';
import SchoolIcon from '@mui/icons-material/School';
import StorageIcon from '@mui/icons-material/Storage';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

import {
  managerPortalRoles,
  learnerPortalRoles,
  adminPortalRoles,
  OPTIX_ACADEMY_LEARNER_READ,
  OPTIX_ACADEMY_MANAGER_READ,
  OPTIX_ACADEMY_ADMIN_READ,
  SCORM_READ,
  TRAINING_COURSE_READ,
  SKILLS_PROGRAMS_READ,
  QUALIFICATIONS_READ,
  USER_READ,
  LEARNERS_READ,
  MODERATORS_READ,
  FACILITATORS_READ,
  ASSESSORS_READ,
  CERTIFICATE_READ,
  LEARNER_ENROLLMENTS_READ,
  TRAINME_COURSES_READ,
  TRAINME_OPTIX_ASSESSMENT_READ,
  SCORM_TEMPLATE_READ,
  TEMPLATES_READ,
  type Role,
} from './roles';
import {routes} from './routes';

export interface NavigationLink {
  path: string;
  icon: ReactNode;
  name: string;
  enableClick?: boolean;
  roles: Role[];
  conditionalRoles?: Role[][];
  disabled?: boolean;
  children?: NavigationLink[];
}

export const homeLinks: NavigationLink[] = [
  {
    name: 'Home',
    icon: <Home />,
    path: routes.landing,
    roles: [],
  },
  {
    name: 'Learner Portal',
    icon: <SchoolIcon />,
    path: routes.learnerPortal.overview,
    roles: learnerPortalRoles,
  },
  {
    name: 'Manager Portal',
    icon: <SupervisorAccountIcon />,
    path: routes.managerPortal.overview,
    roles: managerPortalRoles,
  },
  {
    name: 'Admin Portal',
    icon: <StorageIcon />,
    path: routes.adminPortal.get(),
    roles: adminPortalRoles,
  },
];

export const learnerPortalLinks: NavigationLink[] = [
  {
    name: 'Overview',
    icon: <GridViewIcon />,
    path: routes.learnerPortal.overview,
    roles: learnerPortalRoles,
  },
  {
    name: 'Courses',
    icon: <LightbulbOutlineIcon />,
    path: routes.learnerPortal.courses,
    roles: [OPTIX_ACADEMY_LEARNER_READ, TRAINING_COURSE_READ],
  },
  {
    name: 'Skills Programmes',
    icon: <NoteAltIcon />,
    path: routes.learnerPortal.skillsProgrammes,
    roles: [OPTIX_ACADEMY_LEARNER_READ, SKILLS_PROGRAMS_READ],
  },
  {
    name: 'Qualifications',
    icon: <ContentPasteIcon />,
    path: routes.learnerPortal.qualifications,
    roles: [OPTIX_ACADEMY_LEARNER_READ, QUALIFICATIONS_READ],
  },
  {
    name: 'Certificates',
    icon: <SchoolIcon />,
    path: routes.learnerPortal.certificates,
    roles: [OPTIX_ACADEMY_LEARNER_READ, CERTIFICATE_READ],
  },
];

export const managerPortalLinks: NavigationLink[] = [
  {
    name: 'Overview',
    icon: <GridViewIcon />,
    path: routes.managerPortal.overview,
    roles: managerPortalRoles,
  },
  // Temporarily hidden — re-enable when these pages are ready
  // {
  //   name: 'Modules',
  //   icon: <ViewModuleIcon />,
  //   path: routes.managerPortal.moduleManagement,
  //   roles: [OPTIX_ACADEMY_MANAGER_READ, TRAINME_COURSES_READ],
  //   children: [
  //     {
  //       name: 'View',
  //       icon: <ViewModuleIcon />,
  //       path: routes.managerPortal.moduleManagement,
  //       roles: [OPTIX_ACADEMY_MANAGER_READ, TRAINME_COURSES_READ],
  //     },
  //     {
  //       name: 'Course-Module Links',
  //       icon: <LinkIcon />,
  //       path: routes.managerPortal.courseModules,
  //       roles: [OPTIX_ACADEMY_MANAGER_READ, TRAINME_COURSES_READ],
  //     },
  //   ],
  // },
  // {
  //   name: 'Courses',
  //   icon: <LightbulbOutlineIcon />,
  //   path: routes.managerPortal.courses,
  //   roles: [OPTIX_ACADEMY_MANAGER_READ, TRAINING_COURSE_READ],
  // },
  // {
  //   name: 'Skills Programmes',
  //   icon: <NoteAltIcon />,
  //   path: routes.managerPortal.skillsProgrammes,
  //   roles: [OPTIX_ACADEMY_MANAGER_READ, SKILLS_PROGRAMS_READ],
  // },
  // {
  //   name: 'Qualifications',
  //   icon: <ContentPasteIcon />,
  //   path: routes.managerPortal.qualifications,
  //   roles: [OPTIX_ACADEMY_MANAGER_READ, QUALIFICATIONS_READ],
  // },
  {
    name: 'Certificates',
    icon: <SchoolIcon />,
    path: routes.managerPortal.certificates,
    roles: [OPTIX_ACADEMY_MANAGER_READ, CERTIFICATE_READ],
  },
  // {
  //   name: 'Cohorts',
  //   icon: <GroupsIcon />,
  //   path: routes.managerPortal.cohorts,
  //   roles: managerPortalRoles,
  // },
  {
    name: 'Announcements',
    icon: <CampaignIcon />,
    path: routes.managerPortal.announcements,
    roles: managerPortalRoles,
  },
  {
    name: 'Enrollments',
    icon: <AssignmentIcon />,
    path: routes.managerPortal.enrollments,
    roles: [OPTIX_ACADEMY_MANAGER_READ, LEARNER_ENROLLMENTS_READ],
  },
  {
    name: 'Users',
    icon: <GroupIcon />,
    path: routes.managerPortal.users,
    roles: [],
    conditionalRoles: [
      [OPTIX_ACADEMY_MANAGER_READ, USER_READ],
      [OPTIX_ACADEMY_MANAGER_READ, LEARNERS_READ],
      [OPTIX_ACADEMY_MANAGER_READ, MODERATORS_READ],
      [OPTIX_ACADEMY_MANAGER_READ, FACILITATORS_READ],
      [OPTIX_ACADEMY_MANAGER_READ, ASSESSORS_READ],
    ],
  },
  // Add it back later
  // {
  //   name: 'Scheduler',
  //   icon: <CalendarMonthIcon />,
  //   path: routes.managerPortal.scheduler,
  //   roles: managerPortalRoles,
  // },
];

export const adminPortalLinks: NavigationLink[] = [
  {
    name: 'SCORM Packages',
    icon: <StorageIcon />,
    path: routes.adminPortal.scormTemplates,
    roles: [OPTIX_ACADEMY_ADMIN_READ, SCORM_TEMPLATE_READ],
    children: [
      {
        name: 'Templates',
        icon: <StorageIcon />,
        path: routes.adminPortal.scormTemplates,
        roles: [OPTIX_ACADEMY_ADMIN_READ, SCORM_TEMPLATE_READ],
      },
      {
        name: 'Org-Scoped',
        icon: <StorageIcon />,
        path: routes.adminPortal.scorm,
        roles: [OPTIX_ACADEMY_ADMIN_READ, SCORM_READ],
      },
    ],
  },
  {
    name: 'Modules',
    icon: <ViewModuleIcon />,
    path: routes.adminPortal.moduleManagement,
    roles: [OPTIX_ACADEMY_ADMIN_READ, TRAINME_COURSES_READ],
    children: [
      {
        name: 'View',
        icon: <ViewModuleIcon />,
        path: routes.adminPortal.moduleManagement,
        roles: [OPTIX_ACADEMY_ADMIN_READ, TRAINME_COURSES_READ],
      },
      {
        name: 'Course-Module Links',
        icon: <LinkIcon />,
        path: routes.adminPortal.courseModules,
        roles: [OPTIX_ACADEMY_ADMIN_READ, TRAINME_COURSES_READ],
      },
    ],
  },
  {
    name: 'Courses',
    icon: <LightbulbOutlineIcon />,
    path: routes.adminPortal.courses,
    roles: [OPTIX_ACADEMY_ADMIN_READ, TRAINING_COURSE_READ],
  },
  {
    name: 'Skills Programmes',
    icon: <NoteAltIcon />,
    path: routes.adminPortal.skillsProgrammes,
    roles: [],
    conditionalRoles: [
      [OPTIX_ACADEMY_ADMIN_READ, SKILLS_PROGRAMS_READ],
      [OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ],
    ],
    children: [
      {
        name: 'Templates',
        icon: <NoteAltIcon />,
        path: routes.adminPortal.skillsProgrammeTemplates,
        roles: [OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ],
      },
      {
        name: 'Org-Scoped',
        icon: <NoteAltIcon />,
        path: routes.adminPortal.skillsProgrammes,
        roles: [OPTIX_ACADEMY_ADMIN_READ, SKILLS_PROGRAMS_READ],
      },
    ],
  },
  {
    name: 'Qualifications',
    icon: <ContentPasteIcon />,
    path: routes.adminPortal.qualifications,
    roles: [],
    conditionalRoles: [
      [OPTIX_ACADEMY_ADMIN_READ, QUALIFICATIONS_READ],
      [OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ],
    ],
    children: [
      {
        name: 'Templates',
        icon: <ContentPasteIcon />,
        path: routes.adminPortal.qualificationTemplates,
        roles: [OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ],
      },
      {
        name: 'Org-Scoped',
        icon: <ContentPasteIcon />,
        path: routes.adminPortal.qualifications,
        roles: [OPTIX_ACADEMY_ADMIN_READ, QUALIFICATIONS_READ],
      },
    ],
  },
  {
    name: 'Certificates',
    icon: <SchoolIcon />,
    path: routes.adminPortal.certificates,
    roles: [OPTIX_ACADEMY_ADMIN_READ, CERTIFICATE_READ],
  },
  {
    name: 'Cohorts',
    icon: <GroupsIcon />,
    path: routes.adminPortal.cohorts,
    roles: adminPortalRoles,
  },
  {
    name: 'Assessments',
    icon: <QuizIcon />,
    path: routes.adminPortal.assessments,
    roles: [],
    conditionalRoles: [
      [OPTIX_ACADEMY_ADMIN_READ, TRAINME_OPTIX_ASSESSMENT_READ],
      [OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ],
    ],
    enableClick: true,
    children: [
      {
        name: 'Templates',
        icon: <QuizIcon />,
        path: routes.adminPortal.assessmentTemplates,
        roles: [OPTIX_ACADEMY_ADMIN_READ, TEMPLATES_READ],
      },
      {
        name: 'Org-Scoped',
        icon: <QuizIcon />,
        path: routes.adminPortal.assessments,
        roles: [OPTIX_ACADEMY_ADMIN_READ, TRAINME_OPTIX_ASSESSMENT_READ],
      },
    ],
  },
  {
    name: 'Enrollments',
    icon: <AssignmentIcon />,
    path: routes.adminPortal.enrollments,
    roles: [OPTIX_ACADEMY_ADMIN_READ, LEARNER_ENROLLMENTS_READ],
    children: [
      {
        name: 'View',
        icon: <AssignmentIcon />,
        path: routes.adminPortal.enrollments,
        roles: [OPTIX_ACADEMY_ADMIN_READ, LEARNER_ENROLLMENTS_READ],
      },
      {
        name: 'Create',
        icon: <PlaylistAddIcon />,
        path: routes.adminPortal.createEnrollments,
        roles: [OPTIX_ACADEMY_ADMIN_READ, LEARNER_ENROLLMENTS_READ],
      },
    ],
  },
  {
    name: 'Users',
    icon: <GroupIcon />,
    path: routes.adminPortal.users,
    roles: [],
    conditionalRoles: [
      [OPTIX_ACADEMY_ADMIN_READ, USER_READ],
      [OPTIX_ACADEMY_ADMIN_READ, LEARNERS_READ],
      [OPTIX_ACADEMY_ADMIN_READ, MODERATORS_READ],
      [OPTIX_ACADEMY_ADMIN_READ, FACILITATORS_READ],
      [OPTIX_ACADEMY_ADMIN_READ, ASSESSORS_READ],
    ],
  },
  {
    name: 'Tasks',
    icon: <AssignmentTurnedInIcon />,
    path: routes.adminPortal.standUpTasks,
    roles: adminPortalRoles,
  },
];
